import { Platform, PlatformData, Talent } from '../shared/types';
import { abbreviateNumber } from '../utils/formatNumbers';
import {
    getCountryFlagURLFromCountryOrString,
    getCountryFromLabelOrString,
} from '../utils/countries';

import { LogoIcon } from './LogoIcon';

import { Overline } from '@/components/typography';

interface TalentStatsProps {
    tal: Talent;
}

type PlatformRow = NonNullable<PlatformData[Platform]>;

export default function TalentStats({ tal }: TalentStatsProps) {
    const pd = tal.platformData;
    const ttUser = pd?.TikTok?.username;
    const igUser = pd?.Instagram?.username;
    const ytUser = pd?.YouTube?.username;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-4">
                {tal.contentLanguage ? (
                    <StatPill
                        value={tal.contentLanguage}
                        label="Content Language"
                    />
                ) : null}
                {tal.favoriteGenre ? (
                    <StatPill
                        value={tal.favoriteGenre}
                        label="Favorite Genre"
                    />
                ) : null}
                {tal.gender ? (
                    <StatPill value={tal.gender} label="Gender" />
                ) : null}
            </div>

            {ttUser ? (
                <PlatformStatsSection
                    platform="TikTok"
                    username={ttUser}
                    href={`https://tiktok.com/@${ttUser.toLowerCase()}`}
                >
                    <StatPill
                        value={
                            pd?.TikTok?.followers != null
                                ? abbreviateNumber(pd.TikTok.followers)
                                : '-'
                        }
                        label="Followers"
                    />
                    {pd?.TikTok?.stats ? (
                        <>
                            <StatPill
                                value={
                                    pd.TikTok.stats.averageViews
                                        ? Math.round(
                                              pd.TikTok.stats.averageViews,
                                          ).toLocaleString()
                                        : '-'
                                }
                                label="Avg. Views / Vid"
                            />
                            <StatPill
                                value={
                                    pd.TikTok.stats.engagementRate
                                        ? `${Math.round(pd.TikTok.stats.engagementRate * 1000) / 10}%`
                                        : '-'
                                }
                                label="Engagement Rate"
                            />
                        </>
                    ) : null}
                    {pd?.TikTok ? (
                        <AudienceStats audienceData={pd.TikTok} />
                    ) : null}
                </PlatformStatsSection>
            ) : null}

            {igUser ? (
                <PlatformStatsSection
                    platform="Instagram"
                    username={igUser}
                    href={`https://instagram.com/${igUser}`}
                >
                    {pd?.Instagram?.followers != null ? (
                        <StatPill
                            value={abbreviateNumber(pd.Instagram.followers)}
                            label="Followers"
                        />
                    ) : null}
                    {pd?.Instagram ? (
                        <AudienceStats audienceData={pd.Instagram} />
                    ) : null}
                </PlatformStatsSection>
            ) : null}

            {ytUser ? (
                <PlatformStatsSection
                    platform="YouTube"
                    username={ytUser}
                    href={`https://www.youtube.com/@${ytUser}`}
                >
                    {pd?.YouTube?.followers != null ? (
                        <StatPill
                            value={abbreviateNumber(pd.YouTube.followers)}
                            label="Subscribers"
                        />
                    ) : null}
                </PlatformStatsSection>
            ) : null}
        </div>
    );
}

function PlatformStatsSection({
    platform,
    username,
    href,
    children,
}: {
    platform: Platform;
    username: string;
    href: string;
    children: React.ReactNode;
}) {
    return (
        <section className="space-y-3">
            <div className="flex items-center">
                <a href={href} target="_blank" rel="noreferrer">
                    <LogoIcon brand={platform} className="mr-2 size-8" />
                </a>
                <div className="text-lg font-semibold">
                    @{username.toLowerCase()}
                </div>
            </div>
            <div className="flex flex-wrap gap-4">{children}</div>
        </section>
    );
}

function AudienceStats({ audienceData }: { audienceData: PlatformRow }) {
    const location = audienceData.topFollowerLocation;

    return (
        <>
            <StatPill
                value={`${audienceData.femaleFollowerPct || '-'}%`}
                label="Female Followers"
            />
            <StatPill
                value={`${audienceData.maleFollowerPct || '-'}%`}
                label="Male Followers"
            />
            <StatPill
                value={
                    <span className="inline-flex items-center">
                        {location ? (
                            <img
                                className="mr-2 h-4 w-auto"
                                src={
                                    getCountryFlagURLFromCountryOrString(
                                        location,
                                    ) ?? ''
                                }
                                alt=""
                            />
                        ) : null}
                        {location ? getCountryFromLabelOrString(location) : '-'}
                    </span>
                }
                label="Top Follower Location"
            />
            <StatPill
                value={`${audienceData.topFollowerLocationPct || '-'}%`}
                label="Top Follower Location %"
            />
            <StatPill
                value={audienceData.topFollowerAgeBracket || '-'}
                label="Top Follower Age Bracket"
            />
            <StatPill
                value={`${audienceData.topFollowerAgeBracketPct || '-'}%`}
                label="Top Follower Age Bracket %"
            />
        </>
    );
}

function StatPill({ value, label }: { value: React.ReactNode; label: string }) {
    return (
        <div className="min-w-0">
            <div className="mb-2 inline-flex h-8 items-center justify-center rounded-full bg-muted px-3 text-sm font-bold">
                {value}
            </div>
            <Overline>{label}</Overline>
        </div>
    );
}
